import { useCallback, useRef, useState } from 'react';
import {
  useListAgentConversationsQuery,
  useCreateAgentConversationMutation,
  useDeleteAgentConversationMutation,
  useLazyGetAgentConversationQuery,
  type AgentConversation,
  type AgentMessage,
} from '@/redux/internal-services/agentApi';
import type { TextMessage } from '@/components/chat/MessageBubble';
import type { ConfirmMessage } from '@/components/chat/ConfirmCard';
import type { ActionCardMessage } from '@/components/chat/ActionCard';
import type { CardsMessage } from '@/components/chat/UXView';
import type { Rec } from '@/components/chat/ChatPills';
import type { SamModule } from '@/components/SAM/samActionTypes';
import type { DraftCardMessage } from '@/components/Communications/ComposeCard';
import type { NoteCardMessage } from '@/components/chat/NotePanel';
import type { WorkflowCardMessage } from '@/components/Workflow/WorkflowPanel';

type ChatMessage = TextMessage | CardsMessage | ConfirmMessage | ActionCardMessage | DraftCardMessage | NoteCardMessage | WorkflowCardMessage;

/** How many messages to load per page (initial load + each scroll-up fetch). */
const PAGE_SIZE = 30;

/** Rebuild thread bubbles (text + optional inline action cards) from persisted messages.
 *  Shared by the initial load and scroll-up "load older" so both pages render identically.
 *  Does NOT add the workspace-restore card — that is only relevant to the newest page. */
function buildThreadMessages(persisted: AgentMessage[], inlineCards?: boolean): ChatMessage[] {
  const msgs: ChatMessage[] = [];
  for (const m of persisted) {
    if (m.type === 'agent_action' && m.metadata) {
      const meta = m.metadata as { title?: string; caption?: string; module?: string; filter?: Rec; kind?: string; record_id?: string | null };
      // SAM communication drafts restore as the draft card itself (workspace surface), bound to the
      // persisted communication record — never as a generic action card / list panel. Old steps
      // without a record_id have nothing to restore; skip rather than show a misleading list card.
      if (meta.kind === 'DRAFT_COMMUNICATION') {
        if (meta.record_id) {
          msgs.push({ kind: 'draftcard', id: m.message_id, communication_id: meta.record_id } as DraftCardMessage);
        }
        continue;
      }
      // Workflow surface restore — covers OPEN_ENGAGEMENTS_PANEL with module 'workflow'
      // (list_pending_approvals' approvals-only view, no record_id) and OPEN_WORKFLOW_SETTINGS_PANEL
      // (get_workflow_customization — the tenant-wide structure editor). Engagement-specific panels
      // (OPEN_WORKFLOW_PANEL with a record_id) are NOT restored — engagements are accessed via the
      // property/engagements workspace path, not as a standalone surface.
      if (meta.module === 'workflow') {
        const isSettings = meta.kind === 'OPEN_WORKFLOW_SETTINGS_PANEL';
        const isEngagementSpecific = !isSettings && !!meta.record_id;
        if (isEngagementSpecific) continue; // skip — engagement opens via property path
        msgs.push({
          kind: 'workflowcard',
          id: m.message_id,
          ...(isSettings ? { mode: 'settings' as const } : { engagementId: undefined }),
        } as WorkflowCardMessage);
        continue;
      }
      if (!inlineCards) continue;
      msgs.push({
        kind: 'action',
        id: m.message_id,
        module: (meta.module || 'users') as SamModule,
        filter: (meta.filter || {}) as Rec,
        title: meta.title,
        caption: meta.caption,
      });
    } else {
      msgs.push({
        kind: 'text',
        id: m.message_id,
        role: m.role === 'user' ? 'user' : ('assistant' as 'user' | 'assistant'),
        text: m.content,
        createdAt: m.created_at ? new Date(m.created_at).getTime() : undefined,
        files: Array.isArray(m.files) && m.files.length
          ? (m.files as { name: string; type?: string; url?: string }[])
          : undefined,
      });
    }
  }
  return msgs;
}

export function useConversations(opts: {
  assistantId: string;
  conversationId: string;
  multiThread: boolean;
  /** When true, persisted SAM action cards are also rebuilt inline in the chat thread. */
  inlineCards?: boolean;
  setConversationId: (id: string) => void;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
}): {
  allSessions: AgentConversation[] | undefined;
  loadSession: (id: string) => Promise<void>;
  startNewChat: () => Promise<void>;
  selectSession: (id: string) => void;
  removeSession: (id: string) => Promise<void>;
  /** Fetch the next older page and prepend it to the thread (scroll-up infinite scroll). */
  loadOlder: () => Promise<void>;
  /** Whether older messages remain to be loaded for the active conversation. */
  hasMore: boolean;
  /** True while an older page is being fetched (guards duplicate scroll-up fetches). */
  loadingOlder: boolean;
} {
  const { assistantId, conversationId, multiThread, inlineCards, setConversationId, setMessages } = opts;

  const { data: allSessions } = useListAgentConversationsQuery(undefined, { skip: !multiThread });
  const [createConversation] = useCreateAgentConversationMutation();
  const [deleteConversation] = useDeleteAgentConversationMutation();
  const [fetchConversation] = useLazyGetAgentConversationQuery();

  // Pagination state for the active conversation's thread. `loadedRef` counts how many messages
  // have been pulled from the newest end (the next page's `offset`); `hasMore` gates scroll-up.
  const loadedRef = useRef(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const loadSession = useCallback(async (id: string) => {
    setConversationId(id);
    setMessages([]);
    loadedRef.current = 0;
    setHasMore(false);
    try {
      // Newest page only — older turns stream in via loadOlder() as the user scrolls up.
      const convo = await fetchConversation({ id, limit: PAGE_SIZE, offset: 0 }).unwrap();
      const persisted = convo.messages || [];
      loadedRef.current = persisted.length;
      setHasMore(!!convo.has_more);
      const msgs: ChatMessage[] = buildThreadMessages(persisted, inlineCards);
      // Workspace display is always on: restore the most recent view from the DB-persisted action
      // as a placeholder (hydrate=true); ChatPage fetches its rows and renders it in the panel.
      // Find the most recent action WITH a module (drafts included, so we can tell whether the
      // latest surface should be the draft card or a list). Only a NON-draft latest action gets a
      // hydrate list placeholder — a draft is already restored as its own `draftcard` surface by
      // buildThreadMessages, and must remain the latest workspace surface.
      const lastAction = [...persisted].reverse().find(
        (m) => m.type === 'agent_action' && (m.metadata as { module?: string } | undefined)?.module,
      );
      const lastActionKind = (lastAction?.metadata as { kind?: string } | undefined)?.kind;
      const lastActionModule = (lastAction?.metadata as { module?: string } | undefined)?.module;
      if (lastAction?.metadata && lastActionKind !== 'DRAFT_COMMUNICATION' && lastActionModule !== 'workflow') {
        const meta = lastAction.metadata as { title?: string; caption?: string; module?: string; filter?: Rec };
        // Link the restored view to its turn's assistant reply (id `${run_id}:a`, set by get_session)
        // so the banner above the cards shows the matching response.
        const runId = (lastAction.message_id || '').split(':action:')[0];
        msgs.push({
          kind: 'cards',
          id: `restore-${lastAction.message_id}`,
          module: (meta.module || 'users') as SamModule,
          records: [],
          filter: (meta.filter || {}) as Rec,
          title: meta.title,
          caption: meta.caption,
          hydrate: true,
          messageId: runId ? `${runId}:a` : undefined,
        });
      }
      setMessages(msgs);
      // The workspace "⋯" menu is NOT rebuilt from these messages any more — it reads the deduped
      // `workspace_recent_views` collection via useRecentViews, which also covers views the user
      // opened with no agent turn behind them (a manual filter, a `+` create).
    } catch { /* new/empty session — leave the thread blank */ }
  }, [fetchConversation, inlineCards, setConversationId, setMessages]);

  // Scroll-up "load older": fetch the next page (offset = messages already loaded) and PREPEND its
  // bubbles to the thread. Only text/action bubbles are prepended — the workspace restore card
  // stays owned by loadSession's newest page, and the "⋯" menu pages independently of the thread.
  const loadOlder = useCallback(async () => {
    if (!conversationId || loadingOlder || !hasMore) return;
    setLoadingOlder(true);
    try {
      const convo = await fetchConversation(
        { id: conversationId, limit: PAGE_SIZE, offset: loadedRef.current },
      ).unwrap();
      const persisted = convo.messages || [];
      loadedRef.current += persisted.length;
      setHasMore(!!convo.has_more);
      const older = buildThreadMessages(persisted, inlineCards);
      if (older.length) setMessages((prev) => [...older, ...prev]);
    } catch { /* leave the thread as-is on failure */ }
    finally { setLoadingOlder(false); }
  }, [conversationId, loadingOlder, hasMore, fetchConversation, inlineCards, setMessages]);

  const startNewChat = useCallback(async () => {
    if (!assistantId) return;
    try {
      const convo = await createConversation({ agent_id: assistantId, title: 'New chat' }).unwrap();
      setConversationId(convo.conversation_id || convo.session_id || '');
      setMessages([]);
    } catch { /* surfaced via the offline banner / no-op */ }
  }, [assistantId, createConversation, setConversationId, setMessages]);

  const selectSession = useCallback((id: string) => {
    loadSession(id);
  }, [loadSession]);

  const removeSession = useCallback(async (id: string) => {
    await deleteConversation(id).unwrap().catch(() => undefined);
    if (id === conversationId) {
      setConversationId('');
      setMessages([]);
    }
  }, [deleteConversation, conversationId, setConversationId, setMessages]);

  return {
    allSessions,
    loadSession,
    startNewChat,
    selectSession,
    removeSession,
    loadOlder,
    hasMore,
    loadingOlder,
  };
}
